import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { PageHeader, Spinner, LinkButton } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import { getService } from "@/lib/api";
import { getKeyword } from "@/lib/keywords";
import KeywordLanding from "@/pages/KeywordLanding";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // High-intent service-keyword pages render from static, unique copy.
  const keyword = getKeyword(slug);

  useEffect(() => {
    if (keyword) return; // no DB fetch needed for keyword pages
    setService(null);
    getService(slug).then(setService).catch(() => setNotFound(true));
  }, [slug, keyword]);

  if (keyword) return <KeywordLanding key={slug} kw={keyword} />;

  if (notFound) return <div className="pt-40 pb-40 text-center"><p className="font-display text-4xl">Service not found.</p><Link to="/services" className="text-primary mt-4 inline-block link-underline">Back to services</Link></div>;
  if (!service) return <div className="pt-24"><Spinner /></div>;

  const Icon = Icons[service.icon] || Icons.Sparkles;

  return (
    <div data-testid="service-detail-page">
      <SEO
        title={`${service.title} | Julius Mason`}
        description={service.description || service.tagline}
        path={`/services/${service.slug}`}
        image={service.image}
        lang="en"
        keywords={`${service.title.toLowerCase()}, web design services, julius mason, ${service.tagline}`}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.title,
            description: service.description || service.tagline,
            provider: { "@type": "Person", name: "Julius Mason", url: "https://juliusmason.com/" },
            areaServed: "Worldwide",
            url: `https://juliusmason.com/services/${service.slug}`,
          },
        ]}
      />
      <PageHeader
        chapter={`Services / ${service.title}`}
        titleLines={[service.title]}
        description={service.tagline}
        action={<LinkButton to="/contact" primary>Start a Project <ArrowUpRight className="w-5 h-5" /></LinkButton>}
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Icon className="w-14 h-14 text-primary mb-8" strokeWidth={1.2} />
              <p className="text-xl md:text-2xl leading-relaxed">{service.description}</p>
              <div className="mt-10 aspect-video overflow-hidden border border-border">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <p className="overline text-primary mb-8">What's included</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {(service.features || []).map((f, i) => (
                <Reveal key={i} delay={i * 0.05} className="border border-border p-6 hover:border-primary transition-colors">
                  <Check className="w-5 h-5 text-primary mb-3" />
                  <h3 className="font-display text-xl mb-2">{f.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-primary text-white">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl max-w-2xl leading-[0.95]">Ready to get started with {service.title.toLowerCase()}?</h2>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-primary px-8 py-4 font-semibold hover:bg-white/90 active:scale-95 transition whitespace-nowrap">
            Hire Julius <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-12">
        <Link to="/services" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary"><ArrowLeft className="w-4 h-4" /> All Services</Link>
      </div>
    </div>
  );
}
